import * as Yup from "yup";

/**
 * The three report profiles the API builds (ResidentPdfReportService::TYPE_SECTIONS).
 *
 * `hospital_passport` is the emergency-transfer subset. `care_summary` adds
 * updates; `full_report` adds updates AND governance and is the widest resident
 * assembly in the product — it carries evaluations, care plans and the clinical
 * overview, which no reachable screen renders.
 *
 * Both of the latter two were dead code until 2026-07-27: the payload could not
 * express a type, so every request fell through to the service's
 * `hospital_passport` default.
 */
export const HOSPITAL_REPORT_TYPES = [
  "hospital_passport",
  "care_summary",
  "full_report",
];

/**
 * Menu copy for the three profiles, derived from the section sets above so the
 * picker can never offer a type the API does not build. Order follows
 * `HOSPITAL_REPORT_TYPES` (narrowest → widest); `hospital_passport` carries no
 * explicit type on the wire so it stays the API's own default.
 */
const HOSPITAL_REPORT_TYPE_COPY = {
  hospital_passport: {
    label: "Hospital passport",
    description:
      "Emergency-transfer subset — 48h of observations, top 5 care plans.",
  },
  care_summary: {
    label: "Care summary",
    description:
      "Adds resident updates — 7 days of observations, 30 days of updates.",
  },
  full_report: {
    label: "Full report",
    description:
      "Widest export — adds updates and governance, 30 days of observations.",
  },
};

export const HOSPITAL_REPORT_TYPE_OPTIONS = HOSPITAL_REPORT_TYPES.map(
  (value) => ({ value, ...HOSPITAL_REPORT_TYPE_COPY[value] }),
);

/**
 * Payload for GET `/api/resident/{resident_id}/pdf-report`.
 */
export const exportHospitalReportPayloadSchema = Yup.object({
  resident_id: Yup.string().trim().uuid().required(),
  resident_name: Yup.string().trim().optional(),
  file_name: Yup.string().trim().optional(),
  // Omitted ⇒ the API's own default (hospital_passport), so existing callers
  // are byte-identical.
  type: Yup.string().trim().oneOf(HOSPITAL_REPORT_TYPES).optional(),
});

export const buildHospitalReportUrl = (residentId, type) =>
  `/api/resident/${residentId}/pdf-report${
    type && HOSPITAL_REPORT_TYPES.includes(type)
      ? `?type=${encodeURIComponent(type)}`
      : ""
  }`;

const slugifyFileName = (value) =>
  String(value)
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();

export const buildHospitalReportDownloadName = ({
  resident_name,
  file_name,
  type,
} = {}) => {
  if (file_name) return file_name;
  const slug = resident_name ? slugifyFileName(resident_name) : "resident";
  // The default profile keeps its historic filename; the other two name
  // themselves, so exporting all three for one resident no longer lands three
  // different documents on the same `-hospital-report.pdf`.
  if (!type || type === "hospital_passport") {
    return `${slug}-hospital-report.pdf`;
  }
  return `${slug}-${slugifyFileName(type.replace(/_/g, " "))}.pdf`;
};
